import React, { useMemo, useRef } from 'react';
import { cnMerge } from '../../utils/cnMerge';

export interface RadixOtpInputProps {
    id?: string;
    name?: string;
    label?: string | React.ReactNode;
    value: string;
    length: number;
    error?: string;
    helperText?: string;
    disabled?: boolean;
    className?: string;
    inputClassName?: string;
    fitContainer?: boolean;
    autoComplete?: string;
    onChange: (value: string) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const RadixOtpInput: React.FC<RadixOtpInputProps> = ({
    id,
    name,
    label,
    value,
    length,
    error,
    helperText,
    disabled = false,
    className = '',
    inputClassName = '',
    fitContainer = false,
    autoComplete,
    onChange,
    onBlur,
}) => {
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const safeLength = Math.max(1, length);
    const digits = useMemo(() => value.replace(/\D/g, '').slice(0, safeLength).split(''), [value, safeLength]);

    const labelClasses = cnMerge('block text-base font-medium mb-1', 'text-text-secondary', 'dark:text-text-secondary');

    const baseInputClasses = cnMerge(
        fitContainer ? 'h-12 w-full rounded-md' : 'h-12 w-10 sm:w-12 rounded-md',
        'text-center text-base font-semibold',
        'bg-background-paper',
        'text-text-primary',
        'outline-none focus:outline-none focus:ring-0 focus:ring-offset-0',
        error
            ? 'border-[1px] border-solid border-red-500 dark:border-red-500'
            : 'border-[1px] border-solid border-border-default dark:border-grey-600',
        'transition-all duration-base',
        'dark:bg-background-paper dark:text-text-primary',
        disabled && !inputClassName.includes('opacity') ? 'opacity-50 cursor-not-allowed' : '',
        inputClassName,
    );

    const setCharAt = (idx: number, nextChar: string) => {
        const next = Array.from({ length: safeLength }, (_, i) => digits[i] || '');
        next[idx] = nextChar;
        onChange(next.join(''));
    };

    const handleChange = (idx: number, rawValue: string) => {
        if (disabled) return;
        const numeric = rawValue.replace(/\D/g, '');
        if (!numeric) {
            setCharAt(idx, '');
            return;
        }

        const next = Array.from({ length: safeLength }, (_, i) => digits[i] || '');
        let cursor = idx;
        numeric.split('').forEach((char) => {
            if (cursor < safeLength) {
                next[cursor] = char;
                cursor += 1;
            }
        });
        onChange(next.join(''));

        const focusIndex = Math.min(cursor, safeLength - 1);
        inputRefs.current[focusIndex]?.focus();
        if (cursor > idx + 1 || cursor >= safeLength) {
            inputRefs.current[focusIndex]?.select();
        }
    };

    const handleKeyDown = (idx: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        if (event.key === 'Backspace') {
            if (digits[idx]) {
                setCharAt(idx, '');
                return;
            }
            if (idx > 0) {
                setCharAt(idx - 1, '');
                inputRefs.current[idx - 1]?.focus();
            }
            return;
        }
        if (event.key === 'ArrowLeft' && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
            return;
        }
        if (event.key === 'ArrowRight' && idx < safeLength - 1) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        event.preventDefault();
        const numeric = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, safeLength);
        onChange(numeric);
        const focusIndex = Math.min(Math.max(numeric.length - 1, 0), safeLength - 1);
        inputRefs.current[focusIndex]?.focus();
    };

    return (
        <div className={cnMerge('w-full', className)}>
            {label && (typeof label === 'string' ? <label className={labelClasses}>{label}</label> : label)}
            <div
                className={cnMerge('items-center gap-2', fitContainer ? 'grid w-full' : 'flex')}
                style={fitContainer ? { gridTemplateColumns: `repeat(${safeLength}, minmax(0, 1fr))` } : undefined}
            >
                {Array.from({ length: safeLength }).map((_, idx) => (
                    <input
                        key={idx}
                        ref={(el) => {
                            inputRefs.current[idx] = el;
                        }}
                        id={idx === 0 ? id : undefined}
                        name={name}
                        type="text"
                        inputMode="numeric"
                        autoComplete={autoComplete}
                        maxLength={1}
                        value={digits[idx] || ''}
                        disabled={disabled}
                        className={baseInputClasses}
                        onChange={(event) => handleChange(idx, event.target.value)}
                        onKeyDown={(event) => handleKeyDown(idx, event)}
                        onPaste={handlePaste}
                        onBlur={onBlur}
                    />
                ))}
            </div>
            {error && <p className={cnMerge('mt-1 text-sm text-red-500', 'dark:text-red-400')}>{error}</p>}
            {helperText && !error && (
                <p className={cnMerge('mt-1 text-sm text-text-secondary', 'dark:text-text-secondary')}>{helperText}</p>
            )}
        </div>
    );
};

export default RadixOtpInput;
